import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { format } from "date-fns";

import { useAppSelector } from "@/redux/hooks";
import { Button } from "@/components/Button/Button";
import { Spinner } from "@/components/Spinner/Spinner";

import { privacyPolicySelector } from "../../redux/privacyPolicy/privacyPolicy.slice";
import { useFetchPrivacyPolicyQuery } from "../../redux/privacyPolicy/privacyPolicy.api";

export const PrivacyPolicyPage: FunctionComponent = () => {
	const { t } = useTranslation();
	const { isLoading, isError, error, refetch } = useFetchPrivacyPolicyQuery();
	const { privacyPolicy } = useAppSelector(privacyPolicySelector);

	const isNotFound = isError && error && "status" in error && error.status === 404;

	const formattedDate = privacyPolicy?.createdAt
		? format(new Date(privacyPolicy.createdAt), "MMMM d, yyyy")
		: null;

	if (isLoading) {
		return (
			<div className="flex items-center justify-center min-h-screen bg-brand-white">
				<Spinner color="#4D66F5" />
			</div>
		);
	}

	if (isNotFound) {
		return (
			<div className="flex flex-col items-center justify-center min-h-screen bg-brand-white gap-4">
				<p className="text-text-meta text-base">{t("privacyPolicy.unavailable")}</p>
			</div>
		);
	}

	if (isError) {
		return (
			<div className="flex flex-col items-center justify-center min-h-screen bg-brand-white gap-4">
				<p className="text-text-meta text-base">{t("privacyPolicy.error")}</p>
				<Button title={t("privacyPolicy.retry")} onClick={() => refetch()} />
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-brand-white">
			<div className="max-w-3xl mx-auto px-6 py-12">
				<h1 className="text-text text-3xl font-bold mb-2">{t("privacyPolicy.title")}</h1>

				{formattedDate && (
					<p className="text-text-meta text-sm mb-8">
						{t("privacyPolicy.lastUpdated", { date: formattedDate })}
					</p>
				)}

				<div className="border-t border-border-light mb-8" />

				<div className="prose prose-sm max-w-none text-text [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-4 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-8 [&_h2]:mb-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:mb-4 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_li]:mb-1 [&_a]:text-blue-action [&_a]:underline [&_strong]:font-semibold [&_table]:w-full [&_table]:border-collapse [&_table]:my-4 [&_th]:border [&_th]:border-border-light [&_th]:bg-brand-white [&_th]:px-4 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_td]:border [&_td]:border-border-light [&_td]:px-4 [&_td]:py-2">
					<Markdown remarkPlugins={[remarkGfm]}>{privacyPolicy?.content}</Markdown>
				</div>
			</div>
		</div>
	);
};
